// stores/signatureStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { eSignSOW, fetchSOWeSign } from '../services/api';
import { getCurrentLocation } from '../services/geoLocation';
import { CONSTANTS } from '../constants/constants';
import { isValueEmpty } from '../services/utils';
import { useVeilStore } from './useVeilStore';
import { useDialogStore } from './useDialogStore';
import { queryParamsService } from '../services/queryParams';
import { indexedDBJSONStorage } from './indexedDBStorage';

const initialState = {
  jobsSignatures: {},
  loading: false,
  error: null,
  submitStatus: null,
  locationError: null,
  isSubmitting: false,
  hasHydrated: false
};

export const useSignatureStore = create(
  persist(
    (set, get) => ({
  ...initialState,

  getDefaultSignatureObject: () => {
    return {
      contactPerson: '',
      signature: null,
      timestamp: null,
      location: null,
      comments: '',
      status: CONSTANTS.STATUS.PENDING
    };
  },
  
  // Action to update multiple signature data fields at once
  updateSignatureData: (updates) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, ...updates };
    return { jobsSignatures };
  }),

  initializeContactPersonFromSOW: (sowData) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();

    const sowContactPerson = sowData?.technicianName;
    if (sowContactPerson && isValueEmpty(signatureData.contactPerson)) {
      jobsSignatures[sowKey] = { 
        ...signatureData, 
        contactPerson: sowContactPerson
      };
      return { jobsSignatures };
    }
    
    return state; // No changes
  }),

  // Individual field setters
  setContactPerson: (contactPerson) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, contactPerson };
    return { jobsSignatures };
  }),

  setSignature: (signature) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, signature };
    return { jobsSignatures };
  }),

  setComments: (comments) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, comments };
    return { jobsSignatures };
  }),

  setLocation: (location) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, location };
    return { jobsSignatures };
  }),

  setTimestamp: (timestamp) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, timestamp };
    return { jobsSignatures };
  }),

  setStatus: (status) => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
    
    jobsSignatures[sowKey] = { ...signatureData, status };
    return { jobsSignatures };
  }),

  clearSignature: () => set((state) => {
    let sowKey = queryParamsService.getIssueKey();
    let jobsSignatures = { ...state.jobsSignatures };
    
    jobsSignatures[sowKey] = {
      contactPerson: jobsSignatures[sowKey]?.contactPerson || '',
      signature: null,
      timestamp: null,
      location: null,
      comments: '',
      status: CONSTANTS.STATUS.PENDING
    };
    return { jobsSignatures };
  }),

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setSubmitStatus: (submitStatus) => set({ submitStatus }),
  setLocationError: (locationError) => set({ locationError }),
  setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
  setHasHydrated: (hasHydrated) => set({ hasHydrated }),

  // API Actions
  fetchSignatureData: async (force = false) => {
    let sowKey = queryParamsService.getIssueKey();
    const { getSignatureStatus } = get();

    if (!force && getSignatureStatus(sowKey) !== CONSTANTS.STATUS.NOT_STARTED) {
        return;
    }
    
    set({ loading: true, error: null });
    try {
      const data = await fetchSOWeSign();

      if (isValueEmpty(data)) {
        // Backend has no signature record: clear stale persisted signature state.
        let jobsSignatures = { ...get().jobsSignatures };
        jobsSignatures[sowKey] = get().getDefaultSignatureObject();
        set({
            jobsSignatures,
            loading: false,
            submitStatus: null,
            error: null
        });
        return jobsSignatures[sowKey];
      }

      const payload = data?.eSign || data?.signatureData || data;
      const currentSignatureData = get().jobsSignatures[sowKey] || get().getDefaultSignatureObject();
      const serverSignature = getServerSignature(payload);
      const serverTimestamp = payload?.timestamp || payload?.signedAt || payload?.pickedUpAt;
      const serverContactPerson = payload?.contactPerson || payload?.technicianName || payload?.signedBy;
      const normalizedStatus = normalizeStatus(payload?.status);
      const payloadHasSignatureKey = hasExplicitSignatureKey(payload);
      const shouldClearSignature = payloadHasSignatureKey && isValueEmpty(serverSignature);

      const mergedSignatureData = {
        ...get().getDefaultSignatureObject(),
        ...currentSignatureData,
        contactPerson: !isValueEmpty(serverContactPerson) ? serverContactPerson : currentSignatureData.contactPerson,
        signature: shouldClearSignature
          ? null
          : (!isValueEmpty(serverSignature)
            ? serverSignature
            : normalizeSignatureValue(currentSignatureData.signature)),
        timestamp: !isValueEmpty(serverTimestamp) ? serverTimestamp : currentSignatureData.timestamp,
        location: !isValueEmpty(payload?.location) ? payload.location : currentSignatureData.location,
        comments: !isValueEmpty(payload?.comments) ? payload.comments : currentSignatureData.comments,
        status: !isValueEmpty(normalizedStatus)
          ? normalizedStatus
          : (!isValueEmpty(serverSignature) || (!shouldClearSignature && !isValueEmpty(currentSignatureData.signature))
            ? CONSTANTS.STATUS.COMPLETED
            : currentSignatureData.status)
      };

      let updatedJobsSignatures = { ...get().jobsSignatures };
      updatedJobsSignatures[sowKey] = mergedSignatureData;
      set({
        jobsSignatures: updatedJobsSignatures,
        loading: false,
        submitStatus: null,
        error: null
      });
      return mergedSignatureData;

    } catch (error) {
      set({ 
        error: error.message,
        loading: false
      });
      return null;
    }
  },

  submitSignature: async (t) => {
    let sowKey = queryParamsService.getIssueKey();
    const { jobsSignatures } = get();
    let signatureData = jobsSignatures[sowKey];
    const { showVeil, hideVeil } = useVeilStore.getState();
    const { alert } = useDialogStore.getState();
    showVeil();
    if (!signatureData?.signature) {
      hideVeil();
      set({ submitStatus: { type: 'error', message: t('pleaseProvideSignature') } });
      return;
    }

    if (!signatureData?.contactPerson?.trim()) {
      hideVeil();
      set({ submitStatus: { type: 'error', message: t('pleaseEnterContactPerson') } });
      return;
    }

    set({ isSubmitting: true, submitStatus: null });
    
    try {
      // Add timestamp and location if not present
      const dataToSubmit = {
        ...signatureData,
        timestamp: signatureData.timestamp || new Date().toISOString(),
        status: CONSTANTS.STATUS.COMPLETED
      };

      const response = await eSignSOW(dataToSubmit);
      
      if (response) {
        let updatedJobsSignatures = { ...get().jobsSignatures };
        updatedJobsSignatures[sowKey] = dataToSubmit;
        let message = t('signatureSubmitted');
        if (!isValueEmpty(response.messageKey)) {
            message = t(response.messageKey);
        }
        
        set({ 
          jobsSignatures: updatedJobsSignatures,
          submitStatus: { type: 'success', message: message }
        });
        hideVeil();
        return response;
      }
      throw new Error(response?.message || t('failedToSubmitSignatureBase'));
    } catch (error) {
      set({ 
        submitStatus: { type: 'error', message: error.message }
      });
      hideVeil();
      alert(error.message);
    } finally {
      set({ isSubmitting: false });
    }
  },
  
  getSignatureStatus: (sowKey) => {
    if (isValueEmpty(sowKey)) {
        sowKey = queryParamsService.getIssueKey();
    }

    const { jobsSignatures } = get();
    let signatureData = jobsSignatures[sowKey];
    
    if (!isValueEmpty(signatureData)) {
        if (signatureData.status === CONSTANTS.STATUS.COMPLETED || !isValueEmpty(signatureData.signature)) {
            return CONSTANTS.STATUS.COMPLETED;
        }
        if (!isValueEmpty(signatureData.comments) || !isValueEmpty(signatureData.contactPerson)) {
            return CONSTANTS.STATUS.IN_PROGRESS;
        }
    }
    return CONSTANTS.STATUS.NOT_STARTED;
  },


  // Fetch location with error handling
  fetchLocation: async (t) => {
    let sowKey = queryParamsService.getIssueKey();
    try {
      const location = await getCurrentLocation(t);
      let jobsSignatures = { ...get().jobsSignatures };
      let signatureData = jobsSignatures[sowKey] || get().getDefaultSignatureObject();
      
      jobsSignatures[sowKey] = { ...signatureData, location };
      set({ 
        jobsSignatures,
        locationError: null
      });
      return location;
    } catch (error) {
      set({ locationError: error.message });
      throw error;
    }
  },

  reset: () => set(initialState)
    }),
    {
      name: 'signature-storage',
      storage: indexedDBJSONStorage,
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      }
    }
  )
);


const normalizeStatus = (status) => {
  if (isValueEmpty(status)) {
    return undefined;
  }

  const normalized = String(status).trim().toLowerCase().replaceAll('_', '-');
  if (normalized === 'complete') {
    return CONSTANTS.STATUS.COMPLETED;
  }
  if (normalized === 'inprogress') {
    return CONSTANTS.STATUS.IN_PROGRESS;
  }
  return normalized;
};

const normalizeSignatureValue = (value) => {
  if (isValueEmpty(value) || typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (isValueEmpty(trimmed)) {
    return null;
  }

  if (trimmed.startsWith('data:image/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Backend sometimes sends raw base64 without a data URL prefix.
  const compact = trimmed.replace(/\s/g, '');
  const looksLikeBase64 = compact.length > 120 && /^[A-Za-z0-9+/=]+$/.test(compact);
  if (looksLikeBase64) {
    return `data:image/png;base64,${compact}`;
  }

  return null;
};

const getServerSignature = (payload) => {
  if (typeof payload === 'string') {
    return normalizeSignatureValue(payload);
  }

  return (
    normalizeSignatureValue(payload?.signature) ||
    normalizeSignatureValue(payload?.eSignature) ||
    normalizeSignatureValue(payload?.eSign) ||
    normalizeSignatureValue(payload?.signatureImage) ||
    normalizeSignatureValue(payload?.pickupSignature) ||
    normalizeSignatureValue(payload?.signatureData?.signature)
  );
};

const hasExplicitSignatureKey = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  return [
    'signature',
    'eSignature',
    'eSign',
    'signatureImage',
    'pickupSignature',
    'signatureData'
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));
};

